// dashboard/DashboardStats.tsx
import React from 'react';
import { FileText, Edit3, CheckCircle, Shield } from 'lucide-react';

interface StatsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
  trend?: string;
  trendUp?: boolean;
  subtitle?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  bgColor,
  iconColor,
  trend,
  trendUp,
  subtitle,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-24 transition-all duration-200 hover:shadow-lg cursor-pointer">
      <div className="flex items-start justify-between mb-16">
        <div className={`w-48 h-48 ${bgColor} rounded-xl flex items-center justify-center shadow-sm flex-shrink-0`}>
          <div className={iconColor}>
            {icon}
          </div>
        </div>
      </div>
      <p className="text-14 font-medium text-gray-600 mb-4">{title}</p>
      <p className="text-36 font-semibold text-gray-900 mb-8">{value}</p>
      <div className="flex items-center space-x-4 text-12">
        {trend && (
          <>
            <span className={`font-medium ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
              {trendUp ? '↑' : '↓'} {trend}
            </span>
            <span className="text-gray-500">{subtitle}</span>
          </>
        )}
        {!trend && subtitle && (
          <span className="text-gray-500">{subtitle}</span>
        )}
      </div>
    </div>
  );
};

interface DashboardStatsProps {
  totalDocuments: number;
  pendingSignatures: number;
  completedDocuments: number;
  verificationLevel: string;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({
  totalDocuments,
  pendingSignatures,
  completedDocuments,
  verificationLevel,
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-24 mb-32">
      {/* Total Documents */}
      <StatsCard
        title="Total Documents"
        value={totalDocuments}
        bgColor="bg-blue-50"
        iconColor="text-blue-600"
        trend="12%"
        trendUp={true}
        subtitle="vs last month"
        icon={<FileText className="w-24 h-24" />}
      />

      {/* Pending Signatures */}
      <StatsCard
        title="Pending Signatures"
        value={pendingSignatures}
        bgColor="bg-orange-50"
        iconColor="text-orange-600"
        subtitle={pendingSignatures > 0 ? "Action required" : "All clear"}
        icon={<Edit3 className="w-24 h-24" />}
      />

      {/* Completed Documents */}
      <StatsCard
        title="Completed"
        value={completedDocuments}
        bgColor="bg-green-50"
        iconColor="text-green-600"
        trend="8%"
        trendUp={true}
        subtitle="vs last month"
        icon={<CheckCircle className="w-24 h-24" />}
      />

      {/* Verification Status */}
      <StatsCard
        title="Verification Status"
        value={verificationLevel}
        bgColor="bg-gradient-to-br from-brand-purple to-brand-pink"
        iconColor="text-white"
        subtitle={verificationLevel === 'L1' ? 'Level 1 verified' : 'Level 2 pending'}
        icon={<Shield className="w-24 h-24" />}
      />
    </div>
  );
};
